/* eslint-disable react-hooks/exhaustive-deps */
import React, { useState, useEffect } from "react";
import axios from "axios";
import { Grid, Typography, Box, Button, Chip, Autocomplete, TextField } from "@mui/material";
import KeyboardBackspaceIcon from "@mui/icons-material/KeyboardBackspace";
import { format } from "date-fns";
import SupportTaskCard from "./support-task-card";
import { useSelector } from "react-redux";
import NotificationSnackbar from "../../../common/notificationBar/notification-snackbar";
import "./support-task-list.css";
import Loading from "../../../common/loader/loading";

const SupportTaskList = () => {
  const user = useSelector((state) => state.auth.user);
  const [tasks, setTasks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedTask, setSelectedTask] = useState(null);
  const [timelineFilter, setTimelineFilter] = useState("all"); // Track selected timeline filter
  const [filteredTasks, setFilteredTasks] = useState([]); // Tasks filtered by timeline
  const [openSnackbar, setOpenSnackbar] = useState(false);
  const [statements, setStatements] = useState([]);
  const [childDebriefs, setChildDebriefs] = useState([]);
  const [staffDebriefs, setstaffDebriefs] = useState([]);
  const [newAssigne, setNewAssignee] = useState(selectedTask?.assignee || "");
  const [refresh, setRefresh] = useState(false);
  const [changeAssignee, setChangeAssignee] = useState(false);
  const [roomSearches, setRoomSearches] = useState([]);

  useEffect(() => {
    const fetchRoomSearch = async () => {
      try {
        const response = await axios.get("/api/room/roomSearchAll");

        // If witnessId is set, filter the response
        if (user?.id) {
          const filteredSearches = response.data.filter(
            (search) =>
              search.witnessId === Number(user?.id) && search.dateOfSearch === null && search.status === "pending"
          );
          setRoomSearches(filteredSearches);
        } else {
          setRoomSearches(response.data);
        }
      } catch (error) {
        console.error("Error fetching child debriefs:", error);
      } finally {
        setLoading(false);
      }
    };

    fetchRoomSearch();
  }, [user?.id, refresh]); // Re-fetch when user ID changes

  useEffect(() => {
    const fetchStaffDebriefs = async () => {
      try {
        const response = await axios.get("/api/staff/staffDebriefAll");

        // If witnessId is set, filter the response
        if (user?.id) {
          const filteredDebriefs = response.data.filter(
            (debrief) =>
              debrief.witnessId === Number(user?.id) && debrief.selectedStaff === "" && debrief.status === "pending"
          );
          setstaffDebriefs(filteredDebriefs);
        } else {
          setstaffDebriefs(response.data);
        }
      } catch (error) {
        console.error("Error fetching child debriefs:", error);
      } finally {
        setLoading(false);
      }
    };

    fetchStaffDebriefs();
  }, [user?.id, refresh]); // Re-fetch when user ID changes

  useEffect(() => {
    const fetchChildDebriefs = async () => {
      try {
        const response = await axios.get("/api/child/childDebriefAll");

        // If witnessId is set, filter the response
        if (user?.id) {
          const filteredDebriefs = response.data.filter(
            (debrief) =>
              debrief.witnessId === Number(user?.id) && debrief.witnessData.length === 0 && debrief.status === "pending"
          );
          setChildDebriefs(filteredDebriefs);
        } else {
          setChildDebriefs(response.data);
        }
      } catch (error) {
        console.error("Error fetching child debriefs:", error);
      } finally {
        setLoading(false);
      }
    };

    fetchChildDebriefs();
  }, [user?.id, refresh]); // Re-fetch when user ID changes

  useEffect(() => {
    const fetchSupportStatements = async () => {
      try {
        const response = await axios.get("/api/support/supportStatementAll");

        // If witnessId is set, filter the response
        if (user?.id) {
          const filteredStatements = response.data.filter(
            (statement) =>
              statement.witnessId === Number(user?.id) &&
              statement.whoWasAround === null &&
              statement.status === "pending"
          );
          setStatements(filteredStatements);
        } else {
          setStatements(response.data);
        }
      } catch (error) {
        console.error("Error fetching tasks:", error);
      } finally {
        setLoading(false);
      }
    };

    fetchSupportStatements();
  }, [user?.id, refresh]); // Re-fetch when witnessId changes

  const fetchTasks = async () => {
    setLoading(true);
    try {
      const response = await axios.get("/api/assessments/assetAll", {
        headers: {
          "x-user-role": user?.roleid, // Custom header
        },
      });

      // Filter tasks where taskStatus is "active"
      const activeTasks = (response.data || []).filter(
        (task) => task.taskStatus === "active" && task.status === "pending" && task.assignee === user.username
      );

      setTasks(activeTasks); // Update state with only active tasks
    } catch (error) {
      console.error("Error fetching tasks:", error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchTasks();
  }, [refresh]);

  // Filter tasks based on the selected timeline
  useEffect(() => {
    if (timelineFilter === "all") {
      setFilteredTasks(tasks);
    } else {
      setFilteredTasks(tasks.filter((task) => task.shiftType === timelineFilter));
    }
  }, [tasks, timelineFilter]);

  const handleFilterChange = (filter) => {
    setTimelineFilter(filter);
  };

  const updateTask = (taskId, checklistItem, isChecked) => {
    setTasks((prevTasks) =>
      prevTasks.map((task) =>
        task.id === taskId
          ? {
              ...task,
              checklist: (task.checklist || [])
                .filter((item) => item && item?.item)
                .map((item) =>
                  item?.item === checklistItem?.item ? { ...item, status: isChecked ? "true" : "false" } : item
                ),
            }
          : task
      )
    );
  };

  const handleSubmit = async () => {
    setRefresh(true); // Set refresh state before making the requests

    try {
      // Perform the first API request to update the assessment task
      await axios.put(
        `/api/assessments/asset/${selectedTask?.id}`,
        {
          taskName: selectedTask?.taskName || "",
          description: selectedTask?.description || "",
          timeline: selectedTask?.timeline || "",
          checklist: selectedTask?.checklist?.map((item) => ({
            item: item.item || "",
            status: false,
          })) || [{ item: "", status: false }],
          status: "pending",
          shiftType: selectedTask?.shiftType,
          managed_by: selectedTask?.managed_by || "",
          taskType: selectedTask?.taskType || "manual",
          assignee: newAssigne,
          taskStatus: "active",
          taskOwner: selectedTask?.taskOwner,
        },
        {
          headers: {
            "Content-Type": "application/json",
            "x-user-role": 3,
          },
        }
      );
    } catch (error) {
      console.error("Error updating task or support statement:", error);
      alert("Failed to update. Please try again.");
    }
    setRefresh(false); // Set refresh state before making the requests
  };

  if (loading) {
    return <Loading />;
  }

  return (
    <Grid container className='task-container'>
      <Grid item sm={8} className='task-list'>
        <Box sx={{ p: 4, ml: 2, mr: 1 }}>
          <Typography sx={{ fontSize: "14px" }}>
            <Grid container>
              <Grid item>
                <KeyboardBackspaceIcon />
              </Grid>
              <Grid item> Back to home </Grid>
            </Grid>
          </Typography>
          <Typography sx={{ fontSize: "24px", fontWeight: "bold", color: "black" }} align='left' mt={1}>
            Complete Tasks
          </Typography>
          <Box sx={{ mb: 4, display: "flex", gap: 3 }}>
            <Button
              variant='outlined'
              color='black'
              onClick={() => handleFilterChange("all")}
              sx={{ textTransform: "none", borderColor: timelineFilter === "all" ? "#1976d2" : "#ECECED" }}
            >
              <span
                style={{
                  backgroundColor: timelineFilter === "all" ? "#1976d2" : "#DCDCDE",
                  color: timelineFilter === "all" ? "white" : "black",
                  padding: "0 4px",
                  marginRight: "10px",
                  borderRadius: "4px",
                }}
              >
                {tasks.length}
              </span>{" "}
              All
            </Button>
            <Button
              variant='outlined'
              color='black'
              onClick={() => handleFilterChange("Day Shift")}
              sx={{ textTransform: "none", borderColor: timelineFilter === "Day Shift" ? "#1976d2" : "#ECECED" }}
            >
              <span
                style={{
                  backgroundColor: timelineFilter === "Day Shift" ? "#1976d2" : "#DCDCDE",
                  color: timelineFilter === "Day Shift" ? "white" : "black",
                  padding: "0 4px",
                  marginRight: "10px",
                  borderRadius: "4px",
                }}
              >
                {tasks.filter((task) => task.shiftType === "Day Shift").length}
              </span>{" "}
              Day
            </Button>
            <Button
              variant='outlined'
              color='black'
              onClick={() => handleFilterChange("Night Shift")}
              sx={{ textTransform: "none", borderColor: timelineFilter === "Night Shift" ? "#1976d2" : "#ECECED" }}
            >
              <span
                style={{
                  backgroundColor: timelineFilter === "Night Shift" ? "#1976d2" : "#DCDCDE",
                  color: timelineFilter === "Night Shift" ? "white" : "black",
                  padding: "0 4px",
                  marginRight: "10px",
                  borderRadius: "4px",
                }}
              >
                {tasks.filter((task) => task.shiftType === "Night Shift").length}
              </span>{" "}
              Night
            </Button>
            <Button
              variant='outlined'
              color='black'
              onClick={() => handleFilterChange("Sleep In")}
              sx={{ textTransform: "none", borderColor: timelineFilter === "Sleep In" ? "#1976d2" : "#ECECED" }}
            >
              <span
                style={{
                  backgroundColor: timelineFilter === "Sleep In" ? "#1976d2" : "#DCDCDE",
                  color: timelineFilter === "Sleep In" ? "white" : "black",
                  padding: "0 4px",
                  marginRight: "10px",
                  borderRadius: "4px",
                }}
              >
                {tasks.filter((task) => task.shiftType === "Sleep In").length}
              </span>{" "}
              Sleeping
            </Button>
          </Box>
          <Box className='scrollable-task-list'>
            <SupportTaskCard
              statements={statements}
              childDebriefs={childDebriefs}
              roomSearches={roomSearches}
              staffDebriefs={staffDebriefs}
              tasks={filteredTasks}
              updateTask={updateTask}
              onTaskClick={setSelectedTask}
              refreshTasks={fetchTasks}
              setOpenSnackbar={setOpenSnackbar}
            />
          </Box>
        </Box>
      </Grid>

      <Grid item sm={4}>
        <Box className='detail-view'>
          {selectedTask && (
            <Box sx={{ p: 2, ml: 1, mr: 2 }}>
              <Typography sx={{ fontWeight: "bold", fontSize: "16px", color: "black", mb: 1 }}>
                {selectedTask.taskName}
              </Typography>
              <Typography sx={{ fontSize: "12px", color: "black", mb: 1 }}>{selectedTask.description}</Typography>

              <Grid container spacing={1} alignItems='center' sx={{ mb: 2 }}>
                <Grid item lg={8} md={8} xs={12}>
                  <Typography sx={{ fontSize: "14px" }}>Status</Typography>
                </Grid>
                <Grid item lg={4} md={4} xs={12}>
                  <Chip
                    variant='contained'
                    sx={{
                      fontSize: "12px",
                      borderRadius: 5,
                      backgroundColor: selectedTask.status === "completed" ? "#EAFFF2" : "#F2F6FF",
                      color: selectedTask.status === "completed" ? "green" : "blue",
                    }}
                    label={selectedTask.status}
                    size='small'
                  />
                </Grid>
              </Grid>
              <Grid container spacing={1} alignItems='center' sx={{ mb: 2 }}>
                <Grid item lg={8} md={8} xs={12}>
                  <Typography sx={{ fontSize: "14px" }}>Assigned by</Typography>
                </Grid>
                <Grid item lg={4} md={4} xs={12}>
                  {selectedTask?.assignee !== "unassigned" ? (
                    <Typography sx={{ fontSize: "14px" }}>{selectedTask?.assignee}</Typography>
                  ) : (
                    <Autocomplete
                      id='assignee'
                      options={[user.username]}
                      value={newAssigne}
                      onChange={(event, newValue) => {
                        setNewAssignee(newValue);
                        setChangeAssignee(true);
                      }}
                      renderInput={(params) => (
                        <>
                          <TextField {...params} placeholder='Select' />
                        </>
                      )}
                      fullWidth
                    />
                  )}
                </Grid>
              </Grid>
              <Grid container spacing={1} alignItems='center' sx={{ mb: 2 }}>
                <Grid item lg={8} md={8} xs={12}>
                  <Typography sx={{ fontSize: "14px" }}>Date Created</Typography>
                </Grid>
                <Grid item lg={4} md={4} xs={12}>
                  <Typography variant='body2'>{format(new Date(selectedTask.created_at), "dd MMM yyyy")}</Typography>
                </Grid>
              </Grid>
            </Box>
          )}
          {changeAssignee && (
            <Grid container spacing={1} className='submit-button-container'>
              <Grid item>
                <Button
                  type='submit'
                  variant='contained'
                  color='primary'
                  className='submit-button'
                  sx={{ textTransform: "none" }}
                  onClick={handleSubmit}
                >
                  Update
                </Button>
              </Grid>
            </Grid>
          )}
        </Box>
      </Grid>
      <NotificationSnackbar
        open={openSnackbar}
        onClose={() => setOpenSnackbar(false)}
        color='success'
        message='Task Completed Successfully. '
      />
    </Grid>
  );
};

export default SupportTaskList;
